import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import api from '../../services/api';
import type { CallRefundsPayload } from '../../types';
import { getApiErrorMessage } from '../../utils/errors';

type RefundExecuteState = 'idle' | 'sending' | 'success' | 'error';

interface UseCallRefundsParams {
  callId: string;
  enabled: boolean;
}

interface UseCallRefundsResult {
  data: CallRefundsPayload | null;
  loading: boolean;
  error: string | null;
  executeState: RefundExecuteState;
  executeError: string | null;
  executeRefund: (reason: string) => Promise<boolean>;
  refresh: () => Promise<void>;
}

/**
 * Load one call refund snapshot/history and expose the manual AI refund action.
 */
export function useCallRefunds({ callId, enabled }: UseCallRefundsParams): UseCallRefundsResult {
  const { t } = useTranslation(['components']);
  const [data, setData] = useState<CallRefundsPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [executeState, setExecuteState] = useState<RefundExecuteState>('idle');
  const [executeError, setExecuteError] = useState<string | null>(null);
  const [loadedCallId, setLoadedCallId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    setError(null);
    try {
      const response = await api.get<CallRefundsPayload>(`/api/calls/${callId}/refunds/`);
      setData(response.data ?? null);
      setLoadedCallId(callId);
    } catch (fetchError: unknown) {
      setError(getApiErrorMessage(fetchError, t('components:callDetails.refunds.fetchError')));
    } finally {
      setLoading(false);
    }
  }, [callId, enabled, t]);

  useEffect(() => {
    if (!enabled) return;
    if (loadedCallId === callId) return;
    void refresh();
  }, [callId, enabled, loadedCallId, refresh]);

  useEffect(() => {
    setData(null);
    setError(null);
    setLoading(false);
    setExecuteState('idle');
    setExecuteError(null);
    setLoadedCallId(null);
  }, [callId]);

  const executeRefund = useCallback(async (reason: string) => {
    const normalizedReason = reason.trim();
    if (!normalizedReason) {
      setExecuteState('error');
      setExecuteError(t('components:callDetails.refunds.reasonRequired'));
      return false;
    }

    setExecuteState('sending');
    setExecuteError(null);
    try {
      const response = await api.post<CallRefundsPayload>(`/api/calls/${callId}/refunds/`, { reason: normalizedReason });
      setData(response.data ?? null);
      setLoadedCallId(callId);
      setExecuteState('success');
      return true;
    } catch (requestError: unknown) {
      setExecuteState('error');
      setExecuteError(
        getApiErrorMessage(requestError, t('components:callDetails.refunds.executeError')),
      );
      return false;
    }
  }, [callId, t]);

  return useMemo(() => ({
    data,
    loading,
    error,
    executeState,
    executeError,
    executeRefund,
    refresh,
  }), [data, loading, error, executeState, executeError, executeRefund, refresh]);
}
